/**
 * Browser Fingerprint Utility
 * Generates unique device fingerprints for security tracking
 * Stores raw fingerprint data for comprehensive analysis
 */

/**
 * Generates canvas fingerprint by drawing unique graphics
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 200;
    canvas.height = 50;
    
    // Draw unique graphics that render differently across browsers
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Browser fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Browser fingerprint', 4, 17);
    
    return canvas.toDataURL();
  } catch (error) {
    console.warn('Canvas fingerprint failed:', error);
    return null;
  }
};

/**
 * Extracts WebGL rendering information
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return null;
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: gl.getParameter(debugInfo ? debugInfo.UNMASKED_VENDOR_WEBGL : gl.VENDOR),
      renderer: gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER),
      version: gl.getParameter(gl.VERSION)
    };
  } catch (error) {
    console.warn('WebGL fingerprint failed:', error);
    return null;
  }
};

/**
 * Gets available fonts by measuring rendering differences
 */
const getAvailableFonts = () => {
  try {
    // Common fonts to test for availability
    const testFonts = [
      'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 
      'Verdana', 'Georgia', 'Palatino', 'Garamond', 'Bookman',
      'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact'
    ];
    
    const baseString = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const baseWidth = getTextWidth(baseString, 'monospace');
    const availableFonts = [];
    
    testFonts.forEach(font => {
      const width = getTextWidth(baseString, `${font}, monospace`);
      if (Math.abs(width - baseWidth) > 1) { // Significant difference means font is available
        availableFonts.push(font);
      }
    });
    
    return availableFonts;
  } catch (error) {
    console.warn('Font detection failed:', error);
    return [];
  }
};

/**
 * Helper to measure text width
 */
const getTextWidth = (text, font) => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return context.measureText(text).width;
  } catch (error) {
    return 0;
  }
};

/**
 * Generates comprehensive browser fingerprint with raw data
 */
export const generateBrowserFingerprint = async () => {
  const fingerprint = {};
  
  try {
    // 1. Browser Identification
    fingerprint.userAgent = navigator.userAgent;
    fingerprint.platform = navigator.platform;
    fingerprint.vendor = navigator.vendor;
    
    // 2. Language & Locale
    fingerprint.language = navigator.language;
    fingerprint.languages = navigator.languages;
    fingerprint.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // 3. Screen & Display
    fingerprint.screen = {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight
    };
    
    // 4. Hardware Information
    fingerprint.hardware = {
      concurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory,
      maxTouchPoints: navigator.maxTouchPoints
    };
    
    // 5. Browser Capabilities
    fingerprint.capabilities = {
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      pdfViewerEnabled: navigator.pdfViewerEnabled || false,
      onLine: navigator.onLine
    };
    
    // 6. Graphics & Rendering
    fingerprint.graphics = {
      canvas: getCanvasFingerprint(),
      webgl: getWebGLFingerprint()
    };
    
    // 7. Installed Plugins (limited for performance)
    fingerprint.plugins = Array.from(navigator.plugins)
      .slice(0, 10)
      .map(plugin => ({
        name: plugin.name,
        filename: plugin.filename,
        description: plugin.description
      }));
    
    // 8. Fonts (limited detection for performance)
    fingerprint.fonts = getAvailableFonts().slice(0, 10);
    
    // 9. Connection Information
    if (navigator.connection) {
      fingerprint.connection = {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      };
    }
    
    // 10. Timestamp
    fingerprint.generatedAt = new Date().toISOString();
    
    console.log('Raw browser fingerprint generated successfully');
    return fingerprint;
    
  } catch (error) {
    console.error('Error generating browser fingerprint:', error);
    return null;
  }
};

/**
 * Generates a simple fingerprint as fallback
 */
export const generateSimpleFingerprint = () => {
  try {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hardwareConcurrency: navigator.hardwareConcurrency,
      generatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error generating simple fingerprint:', error);
    return null;
  }
};